/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import axiosConfig from '../../services/axiosConfig';
import CommonAGGridTable from '../AG Grid/CommonAGGridTable';

const { Text, Title } = Typography;

const multiFilter = {
  filter: 'agMultiColumnFilter',
  filterParams: {
    filters: [
      {
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
          buttons: ['reset'],
        },
      },
      {
        filter: 'agSetColumnFilter',
        filterParams: { buttons: ['reset'] },
      },
    ],
  },
};

/* ─────────────────────────────────────────────────────────────────────────────
   Generic master admin page – used for both Consumable and Material
───────────────────────────────────────────────────────────────────────────── */
const MasterAdmin = ({ endpoint, title }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);
  const [form] = Form.useForm();

  /* ── Fetch list ── */
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig.get(`/${endpoint}`);
      const items = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(items) ? items : [];
      setData(list.map((item, i) => ({ ...item, sno: i + 1 })));
    } catch {
      notification.error({ message: `Failed to load ${title} list` });
    } finally {
      setLoading(false);
    }
  };

  /* ── Build AG Grid column defs ── */
  useEffect(() => {
    setColumnDefs([
      {
        headerName: 'S.No',
        field: 'sno',
        maxWidth: 90,
        headerClass: 'ag-center-header',
        cellStyle: { textAlign: 'center' },
        suppressMovable: true,
        lockPosition: true,
        filter: false,
      },
      {
        headerName: 'Category',
        field: 'category',
        width: 200,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 200,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
      },
      {
        headerName: 'Product Code',
        field: 'productCode',
        width: 180,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
      },
      {
        headerName: 'Unit',
        field: 'unit',
        width: 150,
        cellStyle: { textAlign: 'left' },
        suppressMovable: true,
        ...multiFilter,
        cellRenderer: (params) => params.value || '-',
      },
      {
        headerName: 'Actions',
        field: 'actions',
        width: 110,
        cellStyle: { textAlign: 'center' },
        suppressMovable: true,
        lockPosition: true,
        pinned: 'right',
        filter: false,
        cellRenderer: (params) => {
          const record = params.data;
          return (
            <div>
              <Tooltip title="Edit">
                <EditOutlined
                  className="txtIcon"
                  style={{ marginLeft: '10px', fontSize: '18px' }}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <DeleteOutlined
                  className="txtIcon"
                  style={{ marginLeft: '10px', fontSize: '18px' }}
                  onClick={() => handleDeleteClick(record)}
                />
              </Tooltip>
            </div>
          );
        },
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  /* ── Open Add ── */
  const handleAdd = () => {
    setEditRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  /* ── Open Edit ── */
  const handleEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({
      category: record.category,
      name: record.name,
      productCode: record.productCode,
      unit: record.unit ?? '',
    });
    setModalVisible(true);
  };

  /* ── Save (create or update) ── */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editRecord) {
        await axiosConfig.put(`/${endpoint}/${editRecord.id}`, values);
        notification.success({ message: `${title} updated successfully` });
      } else {
        await axiosConfig.post(`/${endpoint}`, values);
        notification.success({ message: `${title} added successfully` });
      }
      setModalVisible(false);
      form.resetFields();
      setEditRecord(null);
      await fetchData();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: `Failed to save ${title}` });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditRecord(null);
  };

  /* ── Open Delete confirm ── */
  const handleDeleteClick = (record) => {
    setDeleteRecord(record);
    setDeleteModalVisible(true);
  };

  /* ── Confirm Delete ── */
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await axiosConfig.delete(`/${endpoint}/${deleteRecord.id}`);
      notification.success({ message: `${title} deleted successfully` });
      setDeleteModalVisible(false);
      setDeleteRecord(null);
      await fetchData();
    } catch {
      notification.error({ message: `Failed to delete ${title}` });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="h-100">
      {/* ── Header ── */}
      <div className="d-flex mt-0.5 justify-content-between tab-content-title">
        <div className="d-flex align-items-center">
          <Title className="mb-0" level={4}>
            {`List of ${title}s`}
          </Title>
        </div>
        <div className="text-right new-calibration">
          <Text onClick={handleAdd} className="ps-3 add-new-calibration" style={{ cursor: 'pointer' }}>
            <span className="icon-22">
              <svg>
                <use xlinkHref="#add" />
              </svg>
            </span>
            {`Add ${title}`}
          </Text>
        </div>
      </div>

      <div className="custom-line" />

      {/* ── AG Grid ── */}
      <div>
        <Row gutter={24}>
          <Col span={24} style={{ padding: '0px 12px 0' }}>
            <CommonAGGridTable
              rowData={data}
              columnDefs={columnDefs}
              activeKeyLine={`List of ${title}s`}
              isRawListLoading={loading}
              loadingMessage={`Loading ${title}s...`}
            />
          </Col>
        </Row>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal
        className="pipeline-modal"
        title={editRecord ? `Edit ${title}` : `Add ${title}`}
        centered
        visible={modalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>
            {saving ? 'Saving...' : editRecord ? 'Update' : 'Add'}
          </Button>,
        ]}
        width="45vw"
      >
        <Form form={form} layout="vertical" autoComplete="off" name={`${endpoint}-form`}>
          <Row gutter={24}>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true, message: 'Category is required' }]}
              >
                <Input className="client-input" placeholder="Enter category" size="large" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input className="client-input" placeholder="Enter name" size="large" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item
                label="Product Code"
                name="productCode"
                rules={[{ required: true, message: 'Product code is required' }]}
              >
                <Input className="client-input" placeholder="Enter product code" size="large" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item label="Unit" name="unit">
                <Input className="client-input" placeholder="e.g. Kg, Bag (25Kg)" size="large" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Delete Confirm Modal ── */}
      <Modal
        className="pipeline-modal"
        title="Confirm Delete"
        centered
        visible={deleteModalVisible}
        onCancel={() => { setDeleteModalVisible(false); setDeleteRecord(null); }}
        footer={[
          <Button key="cancel" onClick={() => { setDeleteModalVisible(false); setDeleteRecord(null); }}>
            Cancel
          </Button>,
          <Button key="delete" type="primary" danger loading={deleting} onClick={handleDeleteConfirm}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to delete <strong>{deleteRecord?.name}</strong>?{' '}
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Named exports – each used as a separate top-level admin tab
───────────────────────────────────────────────────────────────────────────── */
export const ConsumableMasterAdmin = () => (
  <MasterAdmin endpoint="consumable-master" title="Consumable" />
);

export const MaterialMasterAdmin = () => (
  <MasterAdmin endpoint="material-master" title="Material" />
);
